// Cloud adapter for the browser: the only client-side module that talks to the API.
// Every read goes through /api/v1/resources with the same query shape the services already use;
// every change goes through a named server operation at /api/v1/fn/<name>. Auth is a same-origin
// httpOnly cookie set by the sign-in flow, so no token is handled here.
import { config } from "@/config";
import { STREAM_READ_ERROR } from "@/lib/labels";

export type ApiResult<T> = { data: T; error: null } | { data: null; error: ApiError };

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

const BASE = "/api/v1";

function unauthenticated(res: Response) {
  if (res.status === 401) window.dispatchEvent(new CustomEvent("oxley:unauthenticated"));
}

async function readError(res: Response): Promise<string> {
  const j = (await res.json().catch(() => ({}))) as { error?: string; detail?: string };
  return j.error ?? j.detail ?? `Request failed (${res.status})`;
}

/** Calls a server operation (JSON in, JSON out). Aborting `signal` gives up on the request. */
export async function callFn<T = unknown>(name: string, body: unknown = {}, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${config.apiBase}/${name}`, {
    method: "POST",
    signal,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  unauthenticated(res);
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as T;
}

/** Calls a server operation with a raw (non-JSON) body. Content-Type and extra headers are supplied by the caller. */
export async function callFnRaw<T = unknown>(name: string, body: BodyInit, headers: Record<string, string>, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${config.apiBase}/${name}`, {
    method: "POST",
    credentials: "include",
    signal,
    headers,
    body,
  });
  unauthenticated(res);
  if (!res.ok) throw new ApiError(res.status, await readError(res));
  return (await res.json()) as T;
}

/** Calls a streaming operation that emits NDJSON events. */
export async function streamFn(name: string, body: unknown, onEvent: (ev: Record<string, unknown>) => void, signal?: AbortSignal) {
  const res = await fetch(`${config.apiBase}/${name}`, {
    method: "POST",
    signal,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  unauthenticated(res);
  if (!res.ok || !res.body) throw new ApiError(res.status, await readError(res));
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  const handleLine = (text: string) => {
    const line = text.trim();
    if (line) onEvent(parseStreamLine(line));
  };
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) >= 0) {
      const line = buf.slice(0, nl);
      buf = buf.slice(nl + 1);
      handleLine(line);
    }
  }
  // The server may end the stream without a newline after its last line.
  handleLine(buf + dec.decode());
}

function parseStreamLine(line: string): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(line);
  } catch {
    throw new Error(STREAM_READ_ERROR);
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error(STREAM_READ_ERROR);
  return parsed as Record<string, unknown>;
}

// ---- Resource reads: a query builder over GET /api/v1/resources/{table} ----
// Rows are loosely typed here; the services narrow them to the domain types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = any;
type Filter = [col: string, op: string, value: string];

class Query<T = Row[]> implements PromiseLike<ApiResult<T> & { count?: number | null }> {
  private filters: Filter[] = [];
  private orders: string[] = [];
  private cols = "*";
  private lim?: number;
  private off?: number;
  private single = false;
  private countMode?: "exact";
  private mode: "select" | "delete" | "insert" = "select";
  private body: Row[] = [];

  constructor(private table: string) {}

  select(cols = "*", opts?: { count?: "exact"; head?: boolean }) {
    this.cols = cols;
    if (opts?.count) this.countMode = opts.count;
    if (opts?.head) this.lim = 0;
    return this;
  }
  delete() { this.mode = "delete"; return this; }
  /** Inserts rows (POST /api/v1/resources/{table}); the API decides per table who may write. */
  insert(rows: Row | Row[]) { this.mode = "insert"; this.body = Array.isArray(rows) ? rows : [rows]; return this; }

  eq(col: string, v: unknown) { this.filters.push([col, "eq", String(v)]); return this; }
  neq(col: string, v: unknown) { this.filters.push([col, "neq", String(v)]); return this; }
  gt(col: string, v: unknown) { this.filters.push([col, "gt", String(v)]); return this; }
  gte(col: string, v: unknown) { this.filters.push([col, "gte", String(v)]); return this; }
  lt(col: string, v: unknown) { this.filters.push([col, "lt", String(v)]); return this; }
  lte(col: string, v: unknown) { this.filters.push([col, "lte", String(v)]); return this; }
  is(col: string, v: null | boolean) { this.filters.push([col, "is", v === null ? "null" : String(v)]); return this; }
  in(col: string, vs: unknown[]) { this.filters.push([col, "in", `(${vs.map(String).join(",")})`]); return this; }
  order(col: string, opts?: { ascending?: boolean }) { this.orders.push(`${col}.${opts?.ascending === false ? "desc" : "asc"}`); return this; }
  limit(n: number) { this.lim = n; return this; }
  range(from: number, to: number) { this.off = from; this.lim = to - from + 1; return this; }
  maybeSingle() { this.single = true; return this as unknown as Query<Row | null>; }

  private qs(): string {
    const p = new URLSearchParams();
    if (this.cols !== "*") p.set("select", this.cols);
    for (const [c, op, v] of this.filters) p.append(c, `${op}.${v}`);
    for (const o of this.orders) p.append("order", o);
    if (this.lim !== undefined) p.set("limit", String(this.lim));
    if (this.off !== undefined) p.set("offset", String(this.off));
    if (this.single) p.set("single", "1");
    if (this.countMode) p.set("count", this.countMode);
    const s = p.toString();
    return s ? `?${s}` : "";
  }

  async execute(): Promise<ApiResult<T> & { count?: number | null }> {
    const path = `${BASE}/resources/${this.table}${this.qs()}`;
    try {
      const res = this.mode === "insert"
        ? await fetch(`${BASE}/resources/${this.table}`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(this.body) })
        : await fetch(path, { method: this.mode === "delete" ? "DELETE" : "GET", credentials: "include" });
      unauthenticated(res);
      if (!res.ok) return { data: null, error: new ApiError(res.status, await readError(res)) };
      const json = (await res.json()) as { data?: T; count?: number | null };
      return { data: json.data as T, error: null, count: json.count ?? null };
    } catch (e) {
      return { data: null, error: new ApiError(0, e instanceof Error ? e.message : String(e)) };
    }
  }

  then<R1 = ApiResult<T>, R2 = never>(
    onfulfilled?: ((v: ApiResult<T> & { count?: number | null }) => R1 | PromiseLike<R1>) | null,
    onrejected?: ((reason: unknown) => R2 | PromiseLike<R2>) | null,
  ): PromiseLike<R1 | R2> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

/** Calls a database function by name (POST /api/v1/rpc/{name}); result shaped like a query result. */
async function rpc<T = unknown>(name: string, args: Record<string, unknown> = {}): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${BASE}/rpc/${name}`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(args) });
    unauthenticated(res);
    if (!res.ok) return { data: null, error: new ApiError(res.status, await readError(res)) };
    return { data: (await res.json()) as T, error: null };
  } catch (e) {
    return { data: null, error: new ApiError(0, e instanceof Error ? e.message : String(e)) };
  }
}

const api = { from: (table: string) => new Query(table), rpc };
export const db = () => api;

// ---- Auth primitives (wrapped by src/infra/auth/AuthProvider) ----
export interface SessionInfo { userId: string; email: string; name: string | null; memberships: Array<{ tenant_id: string; role: string }>; }
export interface SignInConfig { tenant_id: string; client_id: string; redirect_uri: string; configured: boolean; dev_login: boolean; }

async function readSession(res: Response): Promise<SessionInfo | null> {
  if (!res.ok) return null;
  const j = (await res.json()) as { sub: string; email: string; name: string | null; tenant_ids: string[]; roles_by_tenant: Record<string, string> };
  return { userId: j.sub, email: j.email, name: j.name, memberships: j.tenant_ids.map((t) => ({ tenant_id: t, role: j.roles_by_tenant[t] })) };
}

export const authApi = {
  async config(): Promise<SignInConfig> {
    const res = await fetch(`${BASE}/auth/config`, { credentials: "include", cache: "no-store" });
    return (await res.json()) as SignInConfig;
  },
  async getSession(): Promise<SessionInfo | null> {
    try {
      return await readSession(await fetch(`${BASE}/auth/me`, { credentials: "include" }));
    } catch {
      return null;
    }
  },
  /** Completes a Microsoft sign-in: the id token from MSAL becomes the API's session cookie. */
  async exchangeIdToken(id_token: string): Promise<void> {
    const res = await fetch(`${BASE}/auth/entra/callback`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ id_token }) });
    if (!res.ok) throw new ApiError(res.status, await readError(res));
  },
  /** Local development only; the API refuses it elsewhere. */
  async devLogin(email: string): Promise<void> {
    const res = await fetch(`${BASE}/auth/dev/login`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }) });
    if (!res.ok) throw new ApiError(res.status, await readError(res));
  },
  async signOut() {
    await fetch(`${BASE}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => undefined);
  },
  onChange(cb: () => void) {
    const h = () => cb();
    window.addEventListener("oxley:unauthenticated", h);
    return () => window.removeEventListener("oxley:unauthenticated", h);
  },
};
